import * as Sentry from '@sentry/react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

type FailureSeverity = 'low' | 'medium' | 'high' | 'critical';
type FailureSource = 'frontend' | 'backend' | 'synthetic';

export interface FeatureFailureInput {
  feature: string;
  operation: string;
  surface?: string;
  route?: string;
  organizationId?: string;
  projectId?: string;
  traceId?: string;
  severity?: FailureSeverity;
  failureClass?: string;
  source?: FailureSource;
  userVisible?: boolean;
  retryable?: boolean;
  release?: string;
  message?: string;
  eventId?: string;
  metadata?: Record<string, unknown>;
}

interface ReportFeatureFailureResponse {
  success: boolean;
  failureId: string;
  alerted: boolean;
  traceId: string;
}

const reportFeatureFailure = httpsCallable<FeatureFailureInput, ReportFeatureFailureResponse>(
  functions,
  'reportFeatureFailure'
);

const recentReports = new Map<string, number>();
const LOCAL_DEDUPE_MS = 30_000;
let listenersInitialized = false;

function createTraceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getCurrentRoute(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return `${window.location.pathname}${window.location.search}`;
}

function inferFeatureFromRoute(route?: string): string {
  if (!route) return 'app';
  if (route.startsWith('/project/')) return 'project';
  if (route.startsWith('/portfolio-monitoring')) return 'portfolio-monitoring';
  if (route.startsWith('/portfolio/')) return 'portfolio';
  if (route.startsWith('/documents')) return 'documents';
  if (route.startsWith('/legal')) return 'legal';
  if (route.startsWith('/admin')) return 'admin';
  if (route.startsWith('/dashboard')) return 'dashboard';
  return route.split('/').filter(Boolean)[0] || 'app';
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message || 'Unknown error');
  }
  return 'Unknown error';
}

function getFailureClass(error: unknown): string {
  if (error instanceof Error && error.name) return error.name;
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code?: unknown }).code || 'unknown_error');
  }
  return 'unknown_error';
}

function shouldSkipLocalDuplicate(input: FeatureFailureInput): boolean {
  const key = [
    input.feature,
    input.operation,
    input.failureClass,
    input.message?.slice(0, 180),
    input.route,
  ].join('|');
  const now = Date.now();
  const last = recentReports.get(key) || 0;
  recentReports.set(key, now);
  return now - last < LOCAL_DEDUPE_MS;
}

export async function trackFeatureFailure(
  error: unknown,
  input: FeatureFailureInput
): Promise<void> {
  const route = input.route || getCurrentRoute();
  const feature = input.feature || inferFeatureFromRoute(route);
  const traceId = input.traceId || createTraceId();
  const message = input.message || getErrorMessage(error);
  const failureClass = input.failureClass || getFailureClass(error);
  const release = input.release || import.meta.env.VITE_SENTRY_RELEASE;

  const event: FeatureFailureInput = {
    ...input,
    feature,
    operation: input.operation || 'unknown',
    surface: input.surface || 'web',
    route,
    traceId,
    severity: input.severity || 'medium',
    failureClass,
    source: input.source || 'frontend',
    userVisible: input.userVisible !== false,
    retryable: input.retryable === true,
    release,
    message,
    metadata: {
      ...input.metadata,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    },
  };

  if (shouldSkipLocalDuplicate(event)) {
    return;
  }

  const sentryEventId = error instanceof Error
    ? Sentry.captureException(error, {
        tags: {
          feature: event.feature,
          operation: event.operation,
          failureClass: event.failureClass,
          source: event.source,
          userVisible: String(event.userVisible),
        },
        extra: {
          ...event.metadata,
          route: event.route,
          traceId,
          projectId: event.projectId,
          organizationId: event.organizationId,
        },
      })
    : Sentry.captureMessage(message, {
        level: event.severity === 'critical' ? 'fatal' : event.severity === 'low' ? 'info' : 'error',
        tags: {
          feature: event.feature,
          operation: event.operation,
          failureClass: event.failureClass,
          source: event.source,
          userVisible: String(event.userVisible),
        },
        extra: {
          ...event.metadata,
          route: event.route,
          traceId,
          projectId: event.projectId,
          organizationId: event.organizationId,
        },
      });

  try {
    await reportFeatureFailure({
      ...event,
      eventId: sentryEventId,
    });
  } catch (reportError) {
    console.warn('[FeatureFailure] Failed to report feature failure', reportError);
  }
}

export function initFeatureFailureListeners(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (listenersInitialized) {
    return;
  }
  listenersInitialized = true;

  window.addEventListener('error', (event) => {
    void trackFeatureFailure(event.error || event.message, {
      feature: inferFeatureFromRoute(getCurrentRoute()),
      operation: 'window_error',
      severity: 'high',
      failureClass: event.error instanceof Error ? event.error.name : 'window_error',
      message: event.message,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    void trackFeatureFailure(event.reason, {
      feature: inferFeatureFromRoute(getCurrentRoute()),
      operation: 'unhandled_rejection',
      severity: 'high',
      failureClass: getFailureClass(event.reason),
      message: getErrorMessage(event.reason),
    });
  });
}
